import { RefObject, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { doubleSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import { config } from '../../../../../config/pendo.config.json';

type ScrollHandlerButtonProps = {
  containerRef: RefObject<HTMLDivElement>;
};

const styles = css`
  .rounded-circle {
    bottom: 120px;
    right: ${standardSpacing}px;
    width: ${doubleSpacing}px;
    min-height: ${doubleSpacing}px;
  }
`;

const ScrollHandlerButton = ({ containerRef }: ScrollHandlerButtonProps) => {
  const [isScrollPresent, setIsScrollPresent] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);

  useEffect(() => {
    const element = containerRef.current;
    let resizeObserver: ResizeObserver | null = null;

    const checkScroll = () => {
      if (!element) return;
      setIsScrollPresent(element.scrollHeight > element.clientHeight);
      setIsAtBottom(Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 1);
    };

    if (element) {
      element.addEventListener('scroll', checkScroll);
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(checkScroll);
        resizeObserver.observe(element);
      }
      checkScroll();
    }

    return () => {
      if (element) {
        element.removeEventListener('scroll', checkScroll);
        resizeObserver?.disconnect();
      }
    };
  }, [containerRef]);

  const handleClick = () => {
    const element = containerRef.current;
    if (!element) return;
    element.scrollTo({
      top: isAtBottom ? 0 : element.scrollHeight,
      behavior: 'smooth',
    });
  };

  if (!isScrollPresent) return null;

  return (
    <div css={styles}>
      <ClickableContainer
        className='d-flex bg-gray-2 position-absolute rounded-circle mt-auto ml-auto'
        onClick={handleClick}
        data-qa={config.pendo.activities.aiRolePlay.conversationModal.scrollHandler}
      >
        <NvIcon
          className='text-white d-flex align-items-center m-auto'
          icon={isAtBottom ? 'sorting-up-active' : 'sorting-down-active'}
          size='xss-smallest'
        />
      </ClickableContainer>
    </div>
  );
};

export default ScrollHandlerButton;
